import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Stack } from '@mui/material';
import HookFormTextField from 'src/components/hookform/HookFormTextField';
import BackButton from 'src/components/BackButton';
import SuccessButton from 'src/components/SuccessButton';
import {
  UNMATCHED_FILE_STATUS,
  getUnmatchedFileStatusLabel,
} from 'src/utils/enums/unmatchedFileStatus';

const schema = Yup.object().shape({
  status: Yup.string().required('Status is required'),
  note: Yup.string(),
});

const statusOptions = Object.values(UNMATCHED_FILE_STATUS).map(value => ({
  value,
  label: getUnmatchedFileStatusLabel(value),
}));

const UnmatchedFileStatusForm = ({
  loading,
  onCancel,
  onSubmit,
  initialStatus,
  initialNote,
  submitLabel = 'Save',
}) => {
  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      status: initialStatus || '',
      note: initialNote || '',
    },
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = formMethods;

  useEffect(() => {
    reset({
      status: initialStatus || '',
      note: initialNote || '',
    });
  }, [initialStatus, initialNote, reset]);

  const saveDisabled = loading || !isDirty || !isValid;

  return (
    <form autoComplete="off" onSubmit={handleSubmit(values => onSubmit(values.status, values.note?.trim() || ''))}>
      <Stack spacing={2}>
        <HookFormTextField
          name="status"
          label="Status"
          formMethods={formMethods}
          select
          size="small"
          fullWidth
          options={statusOptions}
        />
        <HookFormTextField
          name="note"
          label="Note"
          formMethods={formMethods}
          placeholder="Enter note"
          size="small"
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
        />
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <BackButton disabled={loading} onClick={onCancel}>
            Close
          </BackButton>
          <SuccessButton type="submit" disabled={saveDisabled} loading={loading}>
            {submitLabel}
          </SuccessButton>
        </Stack>
      </Stack>
    </form>
  );
};

UnmatchedFileStatusForm.propTypes = {
  initialNote: PropTypes.string,
  initialStatus: PropTypes.string,
  loading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitLabel: PropTypes.string,
};

export default UnmatchedFileStatusForm;
